import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Info, Bookmark } from 'lucide-react';
import { NAV_LINKS } from '../mock';
import { useSaved } from '../context/SavedContext';

const DisclaimerBar = () => (
  <div className="w-full bg-[#1A1612] text-[#E6D7A2]/90 text-[11px] leading-none">
    <div className="max-w-7xl mx-auto px-5 py-1.5 flex items-center justify-center gap-1.5 text-center">
      <Info className="w-3 h-3 shrink-0 text-[#CBA344]" />
      <p className="truncate">
        <span className="font-semibold text-[#E6D7A2]">Independent site</span>
        {' '}— not Emirates airline; “Emirates” means the UAE.{' '}
        <Link to="/about" className="underline underline-offset-2 decoration-[#CBA344]/60 hover:text-white">
          More
        </Link>
      </p>
    </div>
  </div>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { count } = useSaved();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50">
      <DisclaimerBar />
      <div
        className={`w-full transition-all duration-300 nav-royal border-b border-[#CBA344]/60 ${
          scrolled ? 'shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)]' : ''
        }`}
      >
        <div className="max-w-7xl mx-auto px-5">
          <div className="flex items-center justify-between h-[72px]">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-5 group">
              <span className="relative flex items-center justify-center w-11 h-11 shrink-0">
                <span className="absolute inset-0 rotate-45 rounded-[5px] gold-gradient shadow-[0_0_0_1px_#0E0F12,0_0_0_2px_#CBA344,0_6px_18px_-6px_rgba(203,163,68,0.8)] group-hover:brightness-110 transition" />
                <span className="relative font-display font-black text-[#0E0F12] text-[14px] leading-none tracking-tight">UAE</span>
              </span>
              <span className="leading-none">
                <span className="block font-display text-[23px] font-black tracking-[0.02em] uppercase gold-text">
                  Emirates Guide
                </span>
                <span className="mt-1 flex items-center gap-1.5 text-[10px] font-bold tracking-[0.24em] uppercase text-[#E6D7A2]">
                  <span className="h-px w-4 bg-[#CBA344]" />Independent · Unaffiliated<span className="h-px w-4 bg-[#CBA344]" />
                </span>
              </span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-8">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) =>
                    `nav-link text-[15px] font-bold uppercase tracking-wide text-[#F7F7F7]/85 hover:text-[#E6D7A2] ${
                      isActive ? 'active text-[#CBA344]' : ''
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>

            {/* CTA + burger */}
            <div className="flex items-center gap-3">
              <Link
                to="/planner"
                aria-label="Trip planner"
                className="relative inline-flex items-center justify-center w-10 h-10 rounded-full text-white hover:bg-white/10 transition-colors"
              >
                <Bookmark className="w-5 h-5" />
                {count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full gold-gradient text-[#0E0F12] text-[11px] font-black">
                    {count}
                  </span>
                )}
              </Link>
              <Link
                to="/hotels"
                className="hidden sm:inline-flex items-center btn-gold rounded-[4px] px-6 py-2.5 text-[13px] uppercase tracking-[0.12em]"
              >
                Find Hotels
              </Link>
              <button
                aria-label="Toggle menu"
                onClick={() => setOpen((v) => !v)}
                className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-white hover:bg-white/10 transition-colors"
              >
                {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 ${
            open ? 'max-h-[460px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <nav className="px-5 pb-6 pt-1 flex flex-col gap-1 border-t border-[#CBA344]/40 nav-royal">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `py-3 px-3 rounded-lg text-[16px] font-bold uppercase tracking-wide transition-colors ${
                    isActive
                      ? 'gold-gradient text-[#0E0F12]'
                      : 'text-[#F7F7F7] hover:bg-white/10'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/planner"
              className="py-3 px-3 rounded-lg text-[16px] font-medium text-[#232528] hover:bg-[#EFE7D2] transition-colors flex items-center gap-2"
            >
              <Bookmark className="w-4 h-4" /> Trip Planner{count > 0 ? ` (${count})` : ''}
            </Link>
            <Link
              to="/hotels"
              className="mt-3 btn-sunset text-center rounded-full px-6 py-3 text-[15px] font-semibold"
            >
              Find Hotels
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
